import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Server, 
  Database, 
  Cloud, 
  Brain,
  Zap,
  Shield,
  Cpu,
  Palette,
  GitBranch,
  Layers
} from 'lucide-react';

const Skills: React.FC = () => {
  const skillCategories = [
    {
      category: 'Programming',
      icon: <Code size={24} />,
      skills: ['Python', 'SQL', 'Java']
    },
    {
      category: 'AI/ML & Agents',
      icon: <Brain size={24} />,
      skills: ['LLMs (OpenAI, Claude, HF)', 'MCP', 'RAG Pipelines', 'LangChain', 'LangGraph', 'Copilot Studio', 'Prompt Engineering', 'PyTorch', 'Embeddings']
    },
    {
      category: 'Backend & APIs',
      icon: <Server size={24} />,
      skills: ['FastAPI', 'Flask', 'Django', 'REST APIs', 'Microservices', 'OAuth2.0']
    },
    {
      category: 'Cloud & DevOps',
      icon: <Cloud size={24} />,
      skills: ['AWS (Lambda, API Gateway, S3, Redshift)', 'Azure', 'Git', 'Kafka', 'Airflow', 'Docker', 'Kubernetes']
    },
    {
      category: 'Databases & Vector Stores',
      icon: <Database size={24} />,
      skills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'ChromaDB', 'Qdrant', 'FAISS']
    }
  ];

  const otherSkills = [
    { name: 'Langfuse', icon: <Zap size={16} /> },
    { name: 'Copilot Studio', icon: <Shield size={16} /> },
    { name: 'PyTorch', icon: <Brain size={16} /> },
    { name: 'Informatica', icon: <Layers size={16} /> },
    { name: 'Airflow', icon: <GitBranch size={16} /> },
    { name: 'Kafka', icon: <Cpu size={16} /> },
    { name: 'Docker', icon: <Layers size={16} /> },
    { name: 'Kubernetes', icon: <Cloud size={16} /> },
    { name: 'OAuth2.0', icon: <Shield size={16} /> },
    { name: 'ChromaDB', icon: <Database size={16} /> },
    { name: 'Qdrant', icon: <Database size={16} /> },
    { name: 'FAISS', icon: <Database size={16} /> },
    { name: 'Microservices', icon: <Server size={16} /> },
    { name: 'Prompt Engineering', icon: <Code size={16} /> }
  ];

  return (
    <section id="skills" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Skills & Expertise
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            AI Engineer with a Master's in Computer Engineering from NYU and 3+ years of professional experience in AI and software development. My expertise spans enterprise-grade AI agents, LLMs, and RAG pipelines, with hands-on experience in agent evaluation, LLM observability, and delivering production-ready AI solutions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-dark-700 p-6 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white">
                  {category.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {category.category}
                </h3>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, skillIndex) => (
                  <motion.span
                    key={skillIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: skillIndex * 0.05 }}
                    viewport={{ once: true }}
                    className="px-3 py-1 bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white dark:bg-dark-700 p-8 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white mx-auto mb-4">
              <Palette size={24} />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Additional Tools &amp; Observability
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Specialized tools and frameworks I use to build, evaluate, and monitor AI systems
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {otherSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="flex flex-col items-center p-4 bg-gray-50 dark:bg-dark-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-500 transition-colors group"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white mb-2 group-hover:scale-110 transition-transform">
                  {skill.icon}
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                  {skill.name}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 